import { doc, setDoc, deleteField } from "firebase/firestore";
import { db, functions, httpsCallable } from "../firebase";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import canvaCreatingRocket from "assets/img/canva-creating-rocket.png";
import { getCanvaRedirectUri } from "./oauthRedirectUri";

/** Canonical manual embed path: /design/{id}/{token}/(view|watch) */
export const CANVA_MANUAL_URL_REGEX = /^https:\/\/www\.canva\.com\/design\/[^/]+\/[^/]+\/(view|watch)$/;
/** Also accepts API-style /design/{id}/(view|watch) without a share token. */
export const CANVA_MANUAL_URL_OPTIONAL_TOKEN_REGEX =
  /^https:\/\/www\.canva\.com\/design\/[^/]+(?:\/[^/]+)?\/(view|watch)$/;
const CANVA_VIEW_LIKE_MODES = new Set(["view", "edit", "present", "preview"]);
const CANVA_WATCH_MODES = new Set(["watch"]);
export const CANVA_NAVIGATION_MODES = {
  SYNC: "sync",
  CANVA: "canva",
};
export const CANVA_VIEW_URL_REFRESH_MS = 20 * 24 * 60 * 60 * 1000;
/** Prefer getCanvaRedirectUri() at call time so host wins over a bad baked env. */
export const DEFAULT_CANVA_REDIRECT_URI = "https://lessonlaunchpad.com/canva-callback";
export { getCanvaRedirectUri };
export const DEFAULT_CANVA_RETURN_PATH = "/";
export const CANVA_CUSTOM_MIN_PX = 40;
export const CANVA_CUSTOM_MAX_PX = 8000;
export const CANVA_CUSTOM_MAX_AREA = 25_000_000;
export const CANVA_FIT_BOX_LONG_SIDE_PX = 1920;

/**
 * Convert a content-box pixel size into a Canva custom design size.
 * Preserves aspect ratio and targets a classroom-friendly long side.
 */
export function buildCanvaDesignSizeFromBox(boxSize = {}, options = {}) {
  const longSideTarget = Math.max(
    CANVA_CUSTOM_MIN_PX,
    Math.min(CANVA_CUSTOM_MAX_PX, Number(options.longSide) || CANVA_FIT_BOX_LONG_SIDE_PX)
  );
  const boxWidth = Number(boxSize?.width);
  const boxHeight = Number(boxSize?.height);

  if (!Number.isFinite(boxWidth) || !Number.isFinite(boxHeight) || boxWidth <= 0 || boxHeight <= 0) {
    return { width: 1920, height: 1080 };
  }

  const aspectRatio = boxWidth / boxHeight;
  let width;
  let height;

  if (aspectRatio >= 1) {
    width = longSideTarget;
    height = Math.round(longSideTarget / aspectRatio);
  } else {
    height = longSideTarget;
    width = Math.round(longSideTarget * aspectRatio);
  }

  width = Math.max(CANVA_CUSTOM_MIN_PX, Math.min(CANVA_CUSTOM_MAX_PX, width));
  height = Math.max(CANVA_CUSTOM_MIN_PX, Math.min(CANVA_CUSTOM_MAX_PX, height));

  if (width * height > CANVA_CUSTOM_MAX_AREA) {
    const scale = Math.sqrt(CANVA_CUSTOM_MAX_AREA / (width * height));
    width = Math.max(CANVA_CUSTOM_MIN_PX, Math.floor(width * scale));
    height = Math.max(CANVA_CUSTOM_MIN_PX, Math.floor(height * scale));
  }

  return { width, height };
}

export function measureDomSize(element) {
  if (!element || typeof element.getBoundingClientRect !== "function") {
    return null;
  }
  const rect = element.getBoundingClientRect();
  const width = Number(rect?.width);
  const height = Number(rect?.height);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return { width, height };
}

export function hasCanvaConnection(user) {
  return Boolean(user?.canvaAuth?.accessToken || user?.canvaAuth?.refreshToken || user?.canvaAuth?.connectedAt);
}

export function hasCanvaDesignWriteScope(user) {
  const scope = String(user?.canvaAuth?.scope || "");
  return scope.split(/\s+/).includes("design:content:write");
}

export function isCanvaReconnectRequiredError(error) {
  const code = String(error?.code || "");
  const message = String(error?.message || "").toLowerCase();
  return (
    code === "functions/failed-precondition" ||
    code === "functions/not-found" ||
    code === "functions/permission-denied" ||
    message.includes("reconnect canva") ||
    message.includes("canva connection expired") ||
    message.includes("no canva refresh token") ||
    message.includes("no canva account is connected") ||
    message.includes("failed to refresh canva access token") ||
    message.includes("grant design editing access")
  );
}

export const getDefaultCanvaNavigationMode = (content) => {
  if (content?.canvaNavigationMode) {
    return content.canvaNavigationMode;
  }

  return CANVA_NAVIGATION_MODES.CANVA;
};

export function stripCanvaEmbedParam(value) {
  return (value || "").trim().replace(/[?&]embed(?:=[^&]*)?(?=#|$)/, "");
}

/** Canva "copy link to this page" uses `#` plus a 1-based page number (`#2`). */
const CANVA_PAGE_HASH_REGEX = /^#\d+$/;

export function getCanvaPageHash(hash) {
  const value = String(hash || "").trim();
  return CANVA_PAGE_HASH_REGEX.test(value) ? value : "";
}

export function getCanvaPageNumber(value) {
  const hash = (() => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("#")) return trimmed;
    try {
      return new URL(trimmed).hash;
    } catch {
      const hashIndex = trimmed.indexOf("#");
      return hashIndex >= 0 ? trimmed.slice(hashIndex) : "";
    }
  })();
  const pageHash = getCanvaPageHash(hash);
  if (!pageHash) return null;
  const pageNumber = parseInt(pageHash.slice(1), 10);
  return Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : null;
}

export function stripCanvaPageHash(value) {
  return String(value || "").trim().replace(/#\d+$/, "");
}

export function applyCanvaPageHash(url, pageNumber) {
  const trimmedUrl = String(url || "").trim();
  if (!trimmedUrl) return "";

  const parsedPageNumber = parseInt(pageNumber, 10);
  const normalizedPageNumber = Number.isFinite(parsedPageNumber) && parsedPageNumber > 0
    ? parsedPageNumber
    : 1;
  return `${stripCanvaPageHash(trimmedUrl)}#${normalizedPageNumber}`;
}

export function isCanvaWatchEmbed(value) {
  const candidate = extractCanvaDesignUrlCandidate(value);
  if (!candidate) return false;

  try {
    const withProtocol = /^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`;
    const parsed = new URL(withProtocol);
    return parsed.pathname.split("/").filter(Boolean).some((part) => part.toLowerCase() === "watch");
  } catch {
    return /\/watch(?:[?#]|$)/i.test(candidate);
  }
}

/**
 * Pull a bare Canva design URL out of pasted text (including iframe HTML).
 */
export function extractCanvaDesignUrlCandidate(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";

  const iframeSrcMatch = trimmed.match(
    /(?:src|href)\s*=\s*["']([^"']*canva\.com\/design[^"']*)["']/i
  );
  if (iframeSrcMatch?.[1]) {
    return iframeSrcMatch[1].trim();
  }

  const bareUrlMatch = trimmed.match(/https?:\/\/(?:www\.)?canva\.com\/design\/[^\s"'<>]+/i);
  if (bareUrlMatch?.[0]) {
    return bareUrlMatch[0].trim();
  }

  if (/^(?:www\.)?canva\.com\/design\//i.test(trimmed)) {
    return `https://${trimmed.replace(/^\/\//, "")}`;
  }

  return trimmed;
}

/**
 * Canva Share → Copy link now produces canva.link short URLs.
 */
export function extractCanvaShortLinkCandidate(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";

  const matched = trimmed.match(/https?:\/\/canva\.link\/[A-Za-z0-9_-]+/i);
  if (matched?.[0]) return matched[0];

  if (/^canva\.link\/[A-Za-z0-9_-]+$/i.test(trimmed)) {
    return `https://${trimmed}`;
  }

  return "";
}

export function isCanvaShortLinkUrl(value) {
  return Boolean(extractCanvaShortLinkCandidate(value));
}

export function isValidCanvaManualUrl(value) {
  return Boolean(normalizeCanvaManualUrl(value));
}

export function buildCanvaDisplayUrl(url) {
  const trimmedUrl = (url || "").trim();
  if (!trimmedUrl) return "";
  return trimmedUrl.includes("?") ? `${trimmedUrl}&embed` : `${trimmedUrl}?embed`;
}

export function buildCanvaDesignDisplayUrl(designId) {
  const trimmedDesignId = String(designId || "").trim();
  if (!trimmedDesignId) return "";
  return `https://www.canva.com/design/${trimmedDesignId}/view?embed`;
}

/**
 * Normalize common Canva share/edit/present links into an embeddable /view or /watch URL.
 * Strips tracking query params, forces www, and rewrites /edit|/present|/preview → /view.
 * Keeps a `#N` page hash so a specific Canva page/slide still opens.
 */
export function normalizeCanvaManualUrl(value) {
  const candidate = extractCanvaDesignUrlCandidate(value);
  if (!candidate) return "";

  let parsed;
  try {
    const withProtocol = /^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`;
    parsed = new URL(withProtocol);
  } catch {
    return "";
  }

  const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
  if (host !== "canva.com") return "";

  const parts = parsed.pathname.split("/").filter(Boolean);
  if (parts[0] !== "design" || parts.length < 2) return "";

  const designId = parts[1];
  if (!designId) return "";

  let shareToken = "";
  let mode = "view";

  if (parts.length === 2) {
    mode = "view";
  } else if (parts.length === 3) {
    const third = String(parts[2] || "").toLowerCase();
    if (CANVA_WATCH_MODES.has(third)) {
      mode = "watch";
    } else if (CANVA_VIEW_LIKE_MODES.has(third)) {
      mode = "view";
    } else {
      // /design/{id}/{token} with no mode — treat as a share token and default to view.
      shareToken = parts[2];
      mode = "view";
    }
  } else {
    shareToken = parts[2];
    const rawMode = String(parts[3] || "").toLowerCase();
    if (CANVA_WATCH_MODES.has(rawMode)) {
      mode = "watch";
    } else if (CANVA_VIEW_LIKE_MODES.has(rawMode) || !rawMode) {
      mode = "view";
    } else {
      return "";
    }
  }

  const cleanPath = shareToken
    ? `https://www.canva.com/design/${designId}/${shareToken}/${mode}`
    : `https://www.canva.com/design/${designId}/${mode}`;

  if (
    !CANVA_MANUAL_URL_REGEX.test(cleanPath) &&
    !CANVA_MANUAL_URL_OPTIONAL_TOKEN_REGEX.test(cleanPath)
  ) {
    return "";
  }

  const displayUrl = buildCanvaDisplayUrl(cleanPath);
  const pageHash = getCanvaPageHash(parsed.hash);
  return pageHash ? `${displayUrl}${pageHash}` : displayUrl;
}

/**
 * Normalize a pasted Canva URL, expanding canva.link short links when needed.
 */
export async function resolveCanvaManualUrl(value) {
  const immediate = normalizeCanvaManualUrl(value);
  if (immediate) {
    return { url: immediate, source: "direct" };
  }

  const shortUrl = extractCanvaShortLinkCandidate(value);
  if (!shortUrl) {
    return { url: "", source: "invalid" };
  }

  const resolveFn = httpsCallable(functions, "resolveCanvaShortLink");
  const response = await resolveFn({ url: shortUrl });
  const resolved = normalizeCanvaManualUrl(response?.data?.resolvedUrl || "");
  return {
    url: resolved,
    source: resolved ? "short_link" : "invalid",
  };
}

export function getCanvaPickerSelection(design, options = {}) {
  const designData = design?.design || design;
  if (!designData?.id) return null;

  const viewUrl = designData?.urls?.view_url || "";
  const canonicalUrl = buildCanvaDesignDisplayUrl(designData.id);
  const fallbackWidth = Number(options.fallbackWidth);
  const fallbackHeight = Number(options.fallbackHeight);
  const thumbnailWidth = Number(designData?.thumbnail?.width) || (
    Number.isFinite(fallbackWidth) && fallbackWidth > 0 ? fallbackWidth : null
  );
  const thumbnailHeight = Number(designData?.thumbnail?.height) || (
    Number.isFinite(fallbackHeight) && fallbackHeight > 0 ? fallbackHeight : null
  );

  return {
    canvaDesignId: designData.id,
    canvaTitle: designData.title || "Untitled Canva Design",
    canvaPageCount: Number.isFinite(Number(designData.page_count)) && Number(designData.page_count) > 0
      ? Number(designData.page_count)
      : null,
    canvaThumbnailUrl: designData?.thumbnail?.url || null,
    canvaThumbnailWidth: thumbnailWidth,
    canvaThumbnailHeight: thumbnailHeight,
    canvaCanonicalUrl: canonicalUrl,
    canvaViewUrl: viewUrl,
    canvaEditUrl: designData?.urls?.edit_url || "",
    canvaSource: "picker",
    canvaViewUrlFetchedAt: null,
    canvaViewUrlExpiresAt: null,
    content: canonicalUrl,
  };
}

/**
 * Aspect ratio for fitting the Canva embed iframe so Canva's black letterbox
 * is replaced by the content-box background.
 * Falls back to 16:9 for known designs without stored thumbnail dimensions.
 */
export function getCanvaEmbedAspectRatio(content = {}) {
  const width = Number(content?.canvaThumbnailWidth);
  const height = Number(content?.canvaThumbnailHeight);
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return width / height;
  }
  if (content?.canvaDesignId || content?.canvaSource === "picker") {
    return 16 / 9;
  }
  return null;
}

export function getCanvaStarter(user) {
  const starter = user?.canvaStarter;
  const designId = String(starter?.designId || "").trim();
  if (!designId) return null;
  return {
    designId,
    title: starter?.title || "My Canva Template",
    thumbnailUrl: starter?.thumbnailUrl || null,
    thumbnailWidth: Number(starter?.thumbnailWidth) || null,
    thumbnailHeight: Number(starter?.thumbnailHeight) || null,
  };
}

export function buildCanvaStarterFromContent(content = {}) {
  const designId = String(content?.canvaDesignId || "").trim();
  if (!designId) return null;
  return {
    designId,
    title: content?.canvaTitle || content?.heading || "My Canva Template",
    thumbnailUrl: content?.canvaThumbnailUrl || null,
    thumbnailWidth: Number(content?.canvaThumbnailWidth) || null,
    thumbnailHeight: Number(content?.canvaThumbnailHeight) || null,
    savedAt: new Date().toISOString(),
  };
}

export async function saveCanvaStarterForUser(uid, starter) {
  const designId = String(starter?.designId || "").trim();
  if (!uid || !designId) {
    throw new Error("A Canva design is required to save a template");
  }

  const payload = {
    designId,
    title: starter?.title || "My Canva Template",
    thumbnailUrl: starter?.thumbnailUrl || null,
    thumbnailWidth: Number(starter?.thumbnailWidth) || null,
    thumbnailHeight: Number(starter?.thumbnailHeight) || null,
    savedAt: starter?.savedAt || new Date().toISOString(),
  };

  await setDoc(
    doc(db, FIREBASE_COLLECTIONS.USERS, uid),
    { canvaStarter: payload },
    { merge: true }
  );

  return payload;
}

export async function clearCanvaStarterForUser(uid) {
  if (!uid) {
    throw new Error("User is required to clear a Canva template");
  }

  await setDoc(
    doc(db, FIREBASE_COLLECTIONS.USERS, uid),
    { canvaStarter: deleteField() },
    { merge: true }
  );
}

export function isCanvaPickerUrlStale(content) {
  return false;
}

export async function beginCanvaAuth(redirectUri = getCanvaRedirectUri()) {
  const beginFn = httpsCallable(functions, "canvaBeginAuth");
  const result = await beginFn({ redirectUri });
  return result.data;
}

export async function disconnectCanva() {
  const disconnectFn = httpsCallable(functions, "canvaDisconnect");
  const result = await disconnectFn();
  return result.data;
}

export async function listCanvaDesigns(options = {}) {
  const listFn = httpsCallable(functions, "canvaListDesigns");
  const result = await listFn(options);
  return result.data;
}

export async function getCanvaDesign(designId) {
  const getFn = httpsCallable(functions, "canvaGetDesign");
  const result = await getFn({ designId });
  return result.data;
}

export async function createCanvaDesign(options = {}) {
  const createFn = httpsCallable(functions, "canvaCreateDesign");
  const result = await createFn(options);
  return result.data;
}

/**
 * Open a placeholder tab during the user gesture so popup blockers allow Canva later.
 */
export function openCanvaEditorTab() {
  if (typeof window === "undefined") return null;
  const tab = window.open("", "_blank");
  if (!tab) return null;

  const loadingMessage = "Opening Canva...";
  const imagePath = String(canvaCreatingRocket || "").trim();
  const absoluteImageUrl = imagePath
    ? (imagePath.startsWith("http") || imagePath.startsWith("data:")
      ? imagePath
      : `${window.location.origin}${imagePath.startsWith("/") ? "" : "/"}${imagePath}`)
    : "";

  try {
    tab.document.title = loadingMessage;
    tab.document.body.style.margin = "0";
    tab.document.body.style.background = "#ffffff";
    tab.document.body.innerHTML = absoluteImageUrl
      ? `
      <div style="font-family: system-ui, -apple-system, sans-serif; display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:100vh; padding:24px; box-sizing:border-box; text-align:center; background:#ffffff;">
        <img
          src="${absoluteImageUrl.replace(/"/g, "&quot;")}"
          alt="${loadingMessage}"
          style="width:min(720px, 100%); height:auto; display:block;"
        />
      </div>
    `
      : `
      <div style="font-family: system-ui, sans-serif; display:flex; align-items:center; justify-content:center; min-height:100vh; color:#004aad; font-weight:600;">
        ${loadingMessage}
      </div>
    `;
  } catch (_) {
    // Some browsers restrict writing to the placeholder tab; navigation still works.
  }

  return tab;
}

export function navigateCanvaEditorTab(tab, url) {
  const targetUrl = String(url || "").trim();
  if (!targetUrl) {
    throw new Error("Missing Canva edit URL");
  }

  if (tab && !tab.closed) {
    tab.location.href = targetUrl;
    try {
      tab.focus();
    } catch (_) {
      // Ignore focus failures.
    }
    return tab;
  }

  const opened = typeof window !== "undefined" ? window.open(targetUrl, "_blank") : null;
  if (!opened) {
    throw new Error("Could not open Canva in a new tab. Please allow popups for Lesson Launchpad.");
  }
  return opened;
}

export function closeCanvaEditorTab(tab) {
  if (tab && !tab.closed) {
    try {
      tab.close();
    } catch (_) {
      // Ignore close failures.
    }
  }
}

export async function prepareCanvaEditRedirect(options = {}) {
  const prepareFn = httpsCallable(functions, "canvaPrepareEditRedirect");
  const result = await prepareFn(options);
  return result.data;
}

export async function resolveCanvaReturn(options = {}) {
  const resolveFn = httpsCallable(functions, "canvaResolveReturn");
  const payload = options.correlationJwt
    ? { correlationJwt: options.correlationJwt }
    : {
        designId: options.designId,
        correlationState: options.correlationState,
      };
  const result = await resolveFn(payload);
  return result.data;
}

export async function refreshCanvaSelection(designId) {
  if (!designId) return null;
  const designResponse = await getCanvaDesign(designId);
  return getCanvaPickerSelection(designResponse?.design);
}

export async function startCanvaEditInCanva({
  editUrl,
  designId,
  classId,
  contentBoxId,
  multiBox = false,
  parentMultiBoxId = "",
  returnPath = DEFAULT_CANVA_RETURN_PATH,
  targetWindow = null,
}) {
  const prepared = await prepareCanvaEditRedirect({
    editUrl,
    designId,
    classId,
    contentBoxId,
    multiBox,
    parentMultiBoxId,
    returnPath,
  });

  if (!prepared?.editUrl) {
    throw new Error("Could not prepare Canva edit redirect");
  }

  navigateCanvaEditorTab(targetWindow, prepared.editUrl);
  return prepared;
}
